import React, { useState, useEffect } from 'react';
import { FileText, Calendar, Download, Edit, Trash2, MoreVertical } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { getUserCVGenerations, deleteCVGeneration, type CVGeneration } from '../lib/cvGenerations';
import { toast } from 'react-hot-toast';

/**
 * Página "Mis CVs" - Lista todas las generaciones guardadas
 */
export function MyCVsPage() {
    const [generations, setGenerations] = useState<CVGeneration[]>([]);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState<any>(null);
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);

    useEffect(() => {
        loadUserAndGenerations();
    }, []);

    const loadUserAndGenerations = async () => {
        try {
            // Obtener usuario
            const { data: { user } } = await supabase.auth.getUser();
            setUser(user);

            if (!user) return;

            // Obtener generaciones
            const result = await getUserCVGenerations(user.id);

            if (result.success && result.data) {
                setGenerations(result.data);
            } else {
                toast.error('Error al cargar tus CVs');
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (generationId: string) => {
        if (!confirm('¿Estás seguro de eliminar este CV? Esta acción no se puede deshacer.')) {
            return;
        }

        const result = await deleteCVGeneration(generationId);

        if (result.success) {
            toast.success('CV eliminado exitosamente');
            // Recargar lista
            setGenerations(prev => prev.filter(g => g.id !== generationId));
        } else {
            toast.error('Error al eliminar CV');
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('es-ES', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    const handleView = (gen: CVGeneration) => {
        // Reconstruir el objeto de datos para la vista previa
        // Manejar posible anidación incorrecta de datos antiguos
        const cvDataObj = gen.cv_data?.cv_data || gen.cv_data;

        const fullData = {
            cv: cvDataObj,
            coverLetter: gen.cover_letter,
            analysis: gen.analysis
        };

        // Guardar en localStorage para que Preview pueda leerlo
        localStorage.setItem('cvData', JSON.stringify(fullData));

        // Navegar a la vista previa
        window.location.href = '/preview';
    };

    const handleEdit = (gen: CVGeneration) => {
        // Para editar, cargamos los datos y vamos al Dashboard (paso de diseño o edición)
        // Por ahora, reutilizamos la lógica de ver pero podríamos redirigir a un paso específico
        handleView(gen);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-950 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="min-h-screen bg-gray-950 flex items-center justify-center">
                <div className="text-center">
                    <p className="text-white text-xl">Debes iniciar sesión para ver tus CVs</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-950 pt-20 pb-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-white mb-2">Mis CVs Generados</h1>
                    <p className="text-gray-400">
                        Todas tus generaciones guardadas en un solo lugar
                    </p>
                </div>

                {/* Lista de CVs */}
                {generations.length === 0 ? (
                    <div className="bg-gradient-to-br from-gray-900/60 to-gray-800/60 backdrop-blur-sm rounded-xl p-12 text-center border border-gray-700/50">
                        <FileText className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-white mb-2">
                            Aún no tienes CVs generados
                        </h3>
                        <p className="text-gray-400 mb-6">
                            Comienza creando tu primer CV optimizado con IA
                        </p>
                        <a
                            href="/dashboard"
                            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-500 hover:to-blue-500 transition-all"
                        >
                            Crear mi primer CV
                        </a>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {generations.map((gen) => {
                            // Extraer info de forma robusta (manejando anidación posible)
                            const data = gen.cv_data?.cv_data || gen.cv_data || {};

                            // Extraer nombre y cargo de forma limpia
                            const rawName = data.name || data.personalInfo?.name || '';
                            const rawTitle = data.title || data.personalInfo?.title || '';

                            // Limpiar textos de caracteres markdown/símbolos
                            const cleanText = (text: string) => {
                                return text
                                    .replace(/[#*]+/g, '') // Eliminar # y *
                                    .replace(/\s+/g, ' ') // Normalizar espacios
                                    .trim();
                            };

                            const nombre = cleanText(rawName);
                            const cargo = cleanText(rawTitle);

                            // Construir título dinámico
                            const displayTitle = nombre && cargo
                                ? `${nombre} | ${cargo}`
                                : nombre || cargo || gen.cv_name || 'CV Generado';

                            return (
                                <div
                                    key={gen.id}
                                    onClick={() => handleView(gen)}
                                    className="group bg-gradient-to-r from-gray-900/80 to-gray-800/60 backdrop-blur-sm rounded-lg border border-gray-700/50 hover:border-purple-500/50 transition-all shadow-sm hover:shadow-purple-500/10 p-4 cursor-pointer"
                                >
                                    <div className="flex items-center gap-4">
                                        {/* Icono de documento */}
                                        <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-gradient-to-br from-purple-600/30 to-blue-600/30 flex items-center justify-center border border-purple-500/30">
                                            <FileText className="w-6 h-6 text-purple-400" />
                                        </div>

                                        {/* Info principal */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="min-w-0">
                                                    <h3 className="text-base font-semibold text-white truncate">
                                                        {displayTitle}
                                                    </h3>
                                                    {gen.company_name && gen.company_name !== 'Thinking' && (
                                                        <p className="text-sm text-gray-400 truncate">
                                                            Para: {gen.company_name}
                                                        </p>
                                                    )}
                                                </div>

                                                {/* Badge de estado */}
                                                <span className={`flex-shrink-0 px-2 py-0.5 text-xs font-medium rounded-full ${gen.status === 'completed'
                                                    ? 'bg-green-600/20 text-green-400 border border-green-600/30'
                                                    : 'bg-yellow-600/20 text-yellow-400 border border-yellow-600/30'
                                                    }`}>
                                                    {gen.status === 'completed' ? 'Completado' : 'Borrador'}
                                                </span>
                                            </div>

                                            {/* Metadata y badges */}
                                            <div className="flex items-center gap-3 mt-2 flex-wrap">
                                                <div className="flex items-center gap-1 text-xs text-gray-500">
                                                    <Calendar className="w-3 h-3" />
                                                    <span>{formatDate(gen.created_at)}</span>
                                                </div>

                                                {gen.company_name && gen.company_name !== 'Thinking' && (
                                                    <span className="text-xs px-2 py-0.5 bg-blue-600/20 text-blue-400 rounded border border-blue-600/30">
                                                        {gen.company_name}
                                                    </span>
                                                )}

                                                {gen.cover_letter && (
                                                    <span className="text-xs px-2 py-0.5 bg-purple-600/20 text-purple-400 rounded border border-purple-600/30">
                                                        + Carta
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Menú de 3 puntos */}
                                        <div className="relative">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setOpenMenuId(openMenuId === gen.id ? null : gen.id);
                                                }}
                                                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-lg transition-colors"
                                            >
                                                <MoreVertical className="w-5 h-5" />
                                            </button>

                                            {/* Dropdown menu */}
                                            {openMenuId === gen.id && (
                                                <div
                                                    className="absolute right-0 top-full mt-1 w-40 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50 py-1"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <button
                                                        onClick={() => {
                                                            handleEdit(gen);
                                                            setOpenMenuId(null);
                                                        }}
                                                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                        Editar
                                                    </button>
                                                    {gen.generated_pdf_url && (
                                                        <a
                                                            href={gen.generated_pdf_url}
                                                            download
                                                            onClick={() => setOpenMenuId(null)}
                                                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                                                        >
                                                            <Download className="w-4 h-4" />
                                                            Descargar PDF
                                                        </a>
                                                    )}
                                                    <button
                                                        onClick={() => {
                                                            handleDelete(gen.id);
                                                            setOpenMenuId(null);
                                                        }}
                                                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-600/20 hover:text-red-300 transition-colors"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                        Eliminar
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
